import PropTypes from "prop-types";
import "../styles.css";
import { useState } from "react";
import React from "react";

const TYPING_INPUT_TYPES = new Set([
  "insertText",
  "deleteContentBackward",
  "deleteContentForward",
  "insertCompositionText",
  "insertFromPaste",
  "insertFromDrop",
  "deleteByCut",
]);

const shouldDeferInputUpdate = (inputType) =>
  inputType && TYPING_INPUT_TYPES.has(inputType);

const clampNumericValue = (value, min, max) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }
  let num = parseInt(String(value), 10);
  if (Number.isNaN(num)) {
    return null;
  }
  if (typeof min === "number" && num < min) {
    num = min;
  }
  if (typeof max === "number" && num > max) {
    num = max;
  }
  return num;
};

// Helper function to convert negative x/y values to 0 for display only
const getDisplayValue = (value) => {
  if (value === "" || value === null || value === undefined) return value;
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num < 0 ? "0" : value;
};
export const convertAbsoluteToRelative = (points, x = 0, y = 0) =>
  points.map((coord, i) => (i % 2 === 0 ? coord - x : coord - y));

export const convertRelativeToAbsolute = (points, x = 0, y = 0) =>
  points.map((coord, i) => (i % 2 === 0 ? coord + x : coord + y));

// Add scalePoints function like LineEditor
function scalePoints(points, newWidth, newHeight) {
  // Find current bounding box
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (let i = 0; i < points.length; i += 2) {
    minX = Math.min(minX, points[i]);
    maxX = Math.max(maxX, points[i]);
    minY = Math.min(minY, points[i + 1]);
    maxY = Math.max(maxY, points[i + 1]);
  }
  const currentWidth = maxX - minX;
  const currentHeight = maxY - minY;
  const scaleX = newWidth / (currentWidth || 1);
  const scaleY = newHeight / (currentHeight || 1);
  // Scale points
  const scaledPoints = points.map((val, idx) =>
    idx % 2 === 0 ? minX + (val - minX) * scaleX : minY + (val - minY) * scaleY
  );
  return scaledPoints;
}

// Add min/max constants for arrow properties
const MIN_X = 0;
const MAX_X = 1000;
const MIN_Y = 0;
const MAX_Y = 1000;
const MIN_STROKE_WIDTH = 1;
const MAX_STROKE_WIDTH = 100;
const MIN_ANGLE = -360;
const MAX_ANGLE = 360;
const MIN_BEND = -1000;
const MAX_BEND = 1000;

function calculateArrowPoints(x, y, length, angleInDegrees) {
  const angleInRadians = (angleInDegrees * Math.PI) / 180;
  const endX = x + Math.cos(angleInRadians) * length;
  const endY = y + Math.sin(angleInRadians) * length;
  return [x, y, endX, endY];
}

function ArrowEditor({
  selectedArrow = null,
  updateArrowProperties,
  waypointCount = 0, // Keep for backward compatibility but not used anymore
  onDeleteArrow,
  canvasHeight,
}) {
  const EDITOR_EDGE_BUFFER_PX = 10;
  const MIN_X = EDITOR_EDGE_BUFFER_PX;
  const MIN_Y = EDITOR_EDGE_BUFFER_PX;
  // Removed spammy selectedArrow log - only log when needed
  const MIN_STROKE_WIDTH = 1;
  const MAX_STROKE_WIDTH = 20;

  const MIN_ROTATION = -360;
  const MAX_ROTATION = 360;

  const MIN_ARROW_SIZE = 1;
  const MAX_ARROW_SIZE = 1000; // Optional, if you support arrow length/size constraints

  // Assuming canvasHeight is a percentage string like "80"
  const canvasPixelHeight = (parseInt(canvasHeight) / 100) * window.innerHeight;

  const MAX_X = Math.max(MIN_X, window.innerWidth - EDITOR_EDGE_BUFFER_PX);
  const MAX_Y = Math.max(MIN_Y, canvasPixelHeight - EDITOR_EDGE_BUFFER_PX);

  // RectangleEditor-style min/max for width/height
  const MIN_WIDTH = 30;
  const MAX_WIDTH = 570;
  const MIN_HEIGHT = 0;
  const MAX_HEIGHT = 600;

  const [arrowPresets, setArrowPresets] = useState(() => {
    const saved = localStorage.getItem("sharedPresets");
    return saved ? JSON.parse(saved) : [];
  });
  const [editingPreset, setEditingPreset] = useState(null);
  const [isStylesExpanded, setIsStylesExpanded] = useState(true);
  const [isPropertiesExpanded, setIsPropertiesExpanded] = useState(true);

  // Local state for input values to avoid immediate updates
  const [localInputValues, setLocalInputValues] = useState({
    x: selectedArrow?.x ? Math.round(selectedArrow.x) : "",
    y: selectedArrow?.y ? Math.round(selectedArrow.y) : "",
    width: selectedArrow?.width ? Math.round(selectedArrow.width) : "",
    height: selectedArrow?.height ? Math.round(selectedArrow.height) : "",
    strokeWidth: selectedArrow?.strokeWidth
      ? Math.round(selectedArrow.strokeWidth)
      : "",
    bends: selectedArrow?.points
      ? Math.max(0, (selectedArrow.points.length - 4) / 2)
      : 0,
    colorHex: selectedArrow?.stroke
      ? selectedArrow.stroke.toUpperCase().replace("#", "")
      : "000000",
  });

  // Update local state when selectedArrow changes
  React.useEffect(() => {
    if (selectedArrow) {
      setLocalInputValues({
        x: selectedArrow.x ? Math.round(selectedArrow.x) : "",
        y: selectedArrow.y ? Math.round(selectedArrow.y) : "",
        width: selectedArrow.width ? Math.round(selectedArrow.width) : "",
        height: selectedArrow.height ? Math.round(selectedArrow.height) : "",
        strokeWidth: selectedArrow.strokeWidth
          ? Math.round(selectedArrow.strokeWidth)
          : "",
        bends: selectedArrow.points
          ? Math.max(0, (selectedArrow.points.length - 4) / 2)
          : 0,
        colorHex: selectedArrow.stroke
          ? selectedArrow.stroke.toUpperCase().replace("#", "")
          : "000000",
      });
    }
  }, [selectedArrow]);

  if (!selectedArrow) return null;

  // Helper for number input: allow empty string, clamp values
  const handleNumberInputWithLimits = (field, value, min, max) => {
    let num = parseInt(value, 10);
    if (value === "" || isNaN(num)) {
      handleArrowPropertyChange(field, min);
      return;
    }
    if (typeof min === "number" && num < min) num = min;
    if (typeof max === "number" && num > max) num = max;
    handleArrowPropertyChange(field, num);
  };

  const handleEnterKey = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.target.blur();
    }
  };

  // Helper to validate and parse hex color
  const validateAndParseHexColor = (hexValue) => {
    // Remove # if present and trim whitespace
    let hex = hexValue.trim().replace(/^#/, "").toUpperCase();

    // Validate hex: must be 3 or 6 characters, only 0-9 and A-F
    if (!/^[0-9A-F]{3}$|^[0-9A-F]{6}$/.test(hex)) {
      return null; // Invalid hex
    }

    // Convert 3-digit to 6-digit if needed
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    return `#${hex}`;
  };

  // Calculate waypoints based on current arrow geometry and desired bend count
  const calculateWaypoints = (currentPoints, bendCount) => {
    if (!currentPoints || currentPoints.length < 4) {
      // Fallback for invalid points
      return [0, 0, 100, 0];
    }

    // Get the actual start and end points of the arrow
    const startX = currentPoints[0];
    const startY = currentPoints[1];
    const endX = currentPoints[currentPoints.length - 2];
    const endY = currentPoints[currentPoints.length - 1];

    if (bendCount === 0) {
      // No bends - return simple start and end points
      return [startX, startY, endX, endY];
    }

    const newPoints = [startX, startY];

    // Calculate the total distance and direction
    const totalDistance = Math.sqrt(
      (endX - startX) ** 2 + (endY - startY) ** 2
    );
    const isHorizontal = Math.abs(endY - startY) < 1; // Consider horizontal if Y difference is very small
    const isVertical = Math.abs(endX - startX) < 1; // Consider vertical if X difference is very small

    // Add intermediate waypoints exactly on the straight line
    for (let i = 1; i <= bendCount; i++) {
      const t = i / (bendCount + 1);
      // Place waypoints exactly on the straight line between start and end
      const waypointX = startX + (endX - startX) * t;
      const waypointY = startY + (endY - startY) * t;

      newPoints.push(waypointX, waypointY);
    }

    newPoints.push(endX, endY);
    return newPoints;
  };

  const {
    id,
    x = 0,
    y = 0,
    width = 100,
    strokeWidth = 2,
    height = 100,
    angle = 90,
    rotation = 0,
    stroke: strokeColor = "#000000",
    isDashed = false,
    bend = 0,
    points,
  } = selectedArrow;

  const handleArrowPropertyChange = (field, value) => {
    // Simple property updates (stroke, fill, etc.)
    if (
      [
        "strokeColor",
        "fillColor",
        "strokeWidth",
        "isDashed",
        "angle",
        "rotation",
      ].includes(field)
    ) {
      updateArrowProperties(selectedArrow.id, { [field]: value });
      return;
    }

    // Handle bends count changes
    if (field === "bends") {
      const bendCount = Math.max(0, Math.floor(value)); // Ensure non-negative integer
      const currentPoints = selectedArrow.points || [0, 0, 0, 0];

      // Calculate current bend count from points array
      // Points array format: [x1, y1, x2, y2, ..., xn, yn]
      // Number of bends = (points.length - 4) / 2
      const currentBendCount = Math.max(0, (currentPoints.length - 4) / 2);

      // Only recalculate waypoints if the bend count actually changed
      if (bendCount === currentBendCount) {
        return; // Don't recalculate if bend count hasn't changed
      }

      const arrowX = selectedArrow.x || 0;
      const arrowY = selectedArrow.y || 0;


      // CRITICAL: Ensure points are relative to arrow position
      // Check if points are absolute by comparing minX to arrowX
      let testMinX = Infinity,
        testMinY = Infinity;
      for (let i = 0; i < currentPoints.length; i += 2) {
        testMinX = Math.min(testMinX, currentPoints[i]);
        testMinY = Math.min(testMinY, currentPoints[i + 1]);
      }

      // If minX is very close to arrowX (within 1px), points are likely absolute
      // Also check if minX equals arrowX exactly (common case)
      const pointsAreAbsolute =
        Math.abs(testMinX - arrowX) < 1 ||
        Math.abs(testMinY - arrowY) < 1 ||
        testMinX === arrowX ||
        testMinY === arrowY;

      const relativePoints = pointsAreAbsolute
        ? convertAbsoluteToRelative(currentPoints, arrowX, arrowY)
        : currentPoints; // Already relative

      // Verify conversion worked - relative points should have minX close to 0 (or negative)
      let verifyMinX = Infinity,
        verifyMinY = Infinity;
      for (let i = 0; i < relativePoints.length; i += 2) {
        verifyMinX = Math.min(verifyMinX, relativePoints[i]);
        verifyMinY = Math.min(verifyMinY, relativePoints[i + 1]);
      }


      // Calculate new waypoints (preserves start and end points)
      // Since calculateWaypoints preserves start and end points, the absolute positions
      // of start and end will be preserved if we keep arrowX and arrowY the same
      let newRelativePoints = calculateWaypoints(relativePoints, bendCount);

      // CRITICAL: Normalize points to start at (0,0) to ensure ArrowElement detects them as relative
      // This prevents ArrowElement from re-converting them and causing jumps
      let normalizedMinX = Infinity,
        normalizedMinY = Infinity;
      for (let i = 0; i < newRelativePoints.length; i += 2) {
        normalizedMinX = Math.min(normalizedMinX, newRelativePoints[i]);
        normalizedMinY = Math.min(normalizedMinY, newRelativePoints[i + 1]);
      }

      // Normalize to start at (0,0)
      const normalizedPoints = newRelativePoints.map((coord, i) =>
        i % 2 === 0 ? coord - normalizedMinX : coord - normalizedMinY
      );

      // Adjust the position calculation to account for normalization
      // The newMinX and newMinY will now be 0 (since we normalized), so we need to adjust
      // the arrow position to preserve the absolute top-left
      const normalizationOffsetX = normalizedMinX;
      const normalizationOffsetY = normalizedMinY;

      newRelativePoints = normalizedPoints;


      // Calculate current bounding box of original points (before waypoint recalculation)
      let oldMinX = Infinity,
        oldMinY = Infinity,
        oldMaxX = -Infinity,
        oldMaxY = -Infinity;
      for (let i = 0; i < relativePoints.length; i += 2) {
        oldMinX = Math.min(oldMinX, relativePoints[i]);
        oldMaxX = Math.max(oldMaxX, relativePoints[i]);
        oldMinY = Math.min(oldMinY, relativePoints[i + 1]);
        oldMaxY = Math.max(oldMaxY, relativePoints[i + 1]);
      }

      // Calculate new bounding box of the normalized waypoints
      // Since we normalized to start at (0,0), newMinX and newMinY should be 0
      let newMinX = Infinity,
        newMinY = Infinity,
        newMaxX = -Infinity,
        newMaxY = -Infinity;
      for (let i = 0; i < newRelativePoints.length; i += 2) {
        newMinX = Math.min(newMinX, newRelativePoints[i]);
        newMaxX = Math.max(newMaxX, newRelativePoints[i]);
        newMinY = Math.min(newMinY, newRelativePoints[i + 1]);
        newMaxY = Math.max(newMaxY, newRelativePoints[i + 1]);
      }

      // Calculate the absolute top-left position of the original bounding box
      // This is what we want to preserve
      const absoluteTopLeftX = arrowX + oldMinX;
      const absoluteTopLeftY = arrowY + oldMinY;

      // Adjust arrow position to preserve absolute top-left when bounding box changes
      // After normalization, newRelativePoints start at (0,0), so newMinX = 0 and newMinY = 0
      // The absolute top-left of the new bounding box should equal the absolute top-left of the old bounding box
      // Formula: newArrowX + newMinX = arrowX + oldMinX
      // Since newMinX = 0 after normalization: newArrowX = arrowX + oldMinX
      // But wait - the new waypoints (before normalization) had minX = normalizedMinX
      // The absolute top-left of the new waypoints (before normalization) was: arrowX + normalizedMinX
      // To preserve the OLD absolute top-left (arrowX + oldMinX) with the NEW normalized points:
      // newArrowX + 0 = arrowX + oldMinX, so newArrowX = arrowX + oldMinX
      // This is correct!
      const newArrowX = arrowX + oldMinX;
      const newArrowY = arrowY + oldMinY;

      // Calculate new dimensions
      const newWidth = newMaxX - newMinX;
      const newHeight = newMaxY - newMinY;



      updateArrowProperties(selectedArrow.id, {
        points: newRelativePoints, // Use new waypoints directly (they're already relative)
        width: newWidth,
        height: newHeight,
        x: newArrowX, // Adjust position to preserve absolute top-left
        y: newArrowY, // Adjust position to preserve absolute top-left
        skipOffset: true, // Prevent position recalculation since we're passing relative points
      });
      return;
    }

    // Position updates (x, y) - just move the group
    if (field === "x" || field === "y") {
      updateArrowProperties(selectedArrow.id, { [field]: value });
      return;
    }

    // Size updates (width, height) - scale all points proportionally to preserve bends
    if (field === "width" || field === "height") {
      // Get current points and position
      let currentPoints = selectedArrow.points || [];
      const currentX = selectedArrow.x || 0;
      const currentY = selectedArrow.y || 0;
      const currentWidth = selectedArrow.width ?? 0;
      const currentHeight = selectedArrow.height ?? 0;

      // CRITICAL: Ensure points are relative to arrow position
      // Check if points are absolute by seeing if they're clearly in absolute coordinate range
      let testMinX = Infinity,
        testMinY = Infinity,
        testMaxX = -Infinity,
        testMaxY = -Infinity;
      for (let i = 0; i < currentPoints.length; i += 2) {
        testMinX = Math.min(testMinX, currentPoints[i]);
        testMaxX = Math.max(testMaxX, currentPoints[i]);
        testMinY = Math.min(testMinY, currentPoints[i + 1]);
        testMaxY = Math.max(testMaxY, currentPoints[i + 1]);
      }

      // Points are likely absolute if:
      // 1. minX/minY are very close to currentX/currentY (within 1px) - direct match
      // 2. OR values are large (> 200) and when we subtract currentX/Y, we get more reasonable relative coords
      //    (relative coords typically have smaller magnitude, often negative min for non-normalized)
      const closeToCurrent = Math.abs(testMinX - currentX) < 1 || Math.abs(testMinY - currentY) < 1;
      
      // Try converting to see if it makes more sense as relative
      const testRelativeMinX = testMinX - currentX;
      const testRelativeMinY = testMinY - currentY;
      const wouldBeReasonableRelative = 
        (testMaxX > 200 && Math.abs(testRelativeMinX) < Math.abs(testMinX)) ||
        (testMaxY > 200 && Math.abs(testRelativeMinY) < Math.abs(testMinY));
      
      const pointsAreAbsolute = closeToCurrent || (testMaxX > 200 && wouldBeReasonableRelative);

      if (pointsAreAbsolute) {
        // Convert absolute points to relative
        currentPoints = currentPoints.map((coord, i) =>
          i % 2 === 0 ? coord - currentX : coord - currentY
        );
      }

      // CRITICAL: Points should already be normalized to start at (0,0) relative to arrow position
      // (This is done in arrowRenderer.js onUpdate callback)
      // But we need to handle the case where they might not be normalized (for backward compatibility)
      let originalMinX = Infinity,
        originalMinY = Infinity,
        originalMaxX = -Infinity,
        originalMaxY = -Infinity;
      for (let i = 0; i < currentPoints.length; i += 2) {
        originalMinX = Math.min(originalMinX, currentPoints[i]);
        originalMaxX = Math.max(originalMaxX, currentPoints[i]);
        originalMinY = Math.min(originalMinY, currentPoints[i + 1]);
        originalMaxY = Math.max(originalMaxY, currentPoints[i + 1]);
      }

      // Calculate the absolute top-left position of the bounding box
      // This is what we want to preserve throughout the operation
      const absoluteTopLeftX = currentX + originalMinX;
      const absoluteTopLeftY = currentY + originalMinY;

      // Check if points are normalized (minX and minY are close to 0)
      const pointsAreNormalized =
        Math.abs(originalMinX) < 0.001 && Math.abs(originalMinY) < 0.001;

      let normalizedPoints, minX, minY, maxX, maxY, normalizedX, normalizedY;

      if (pointsAreNormalized) {
        // Points are already normalized - use them as-is
        normalizedPoints = currentPoints;
        minX = 0;
        minY = 0;
        maxX = originalMaxX;
        maxY = originalMaxY;
        normalizedX = currentX;
        normalizedY = currentY;
      } else {
        // Points are NOT normalized - work with them as-is without normalizing
        // This prevents visual jumping when changing width/height
        normalizedPoints = currentPoints; // Keep points as-is
        minX = originalMinX;
        minY = originalMinY;
        maxX = originalMaxX;
        maxY = originalMaxY;
        normalizedX = currentX; // Keep arrow position unchanged
        normalizedY = currentY; // Keep arrow position unchanged
      }

      // Use normalized points for scaling
      currentPoints = normalizedPoints;

      // Calculate scale factors
      // If currentWidth/Height is 0 or invalid, use the actual calculated dimensions from points
      const actualWidth = originalMaxX - originalMinX;
      const actualHeight = originalMaxY - originalMinY;
      
      // For width/height changes, we need a valid dimension to scale from
      // If both stored and actual dimensions are 0 or invalid, we can't scale proportionally
      // In that case, we'll need to handle it specially (set directly)
      const scaleX =
        field === "width" 
          ? (currentWidth > 0 ? value / currentWidth : (actualWidth > 0.001 ? value / actualWidth : 1))
          : 1;
      const scaleY =
        field === "height"
          ? (currentHeight > 0 ? value / currentHeight : (actualHeight > 0.001 ? value / actualHeight : 1))
          : 1;
      
      // Special case: if we're trying to set height but can't scale (currentHeight and actualHeight are 0 or very small),
      // we need to set the height directly by adjusting the Y coordinates
      if (field === "height" && value > 0 && scaleY === 1 && (currentHeight <= 0 && actualHeight <= 0.001)) {
        // This is a horizontal arrow - set height directly
        // Keep start point Y, adjust end point Y to create the desired height
        if (currentPoints.length >= 4) {
          const startY = currentPoints[1];
          // Determine direction: if end point is to the right, height goes down (positive)
          // For a horizontal arrow, we'll set height in the positive direction
          const endY = startY + value;
          const newPoints = [...currentPoints];
          // Update all Y coordinates proportionally, but ensure end point has the desired height
          for (let i = 1; i < newPoints.length; i += 2) {
            if (i === 1) {
              // Keep start Y
              newPoints[i] = startY;
            } else if (i === newPoints.length - 1) {
              // Set end Y to create desired height
              newPoints[i] = endY;
            } else {
              // For waypoints, interpolate proportionally (though for horizontal arrow, they should be same Y)
              newPoints[i] = startY;
            }
          }
          
          // Update the arrow with the new points
          updateArrowProperties(selectedArrow.id, {
            height: value,
            points: newPoints,
            width: actualWidth > 0 ? actualWidth : selectedArrow.width,
            skipOffset: true,
          });
          return;
        }
      }
      
      // Similar special case for width
      if (field === "width" && currentWidth <= 0 && actualWidth <= 0.001 && value > 0) {
        // This is a vertical arrow - set width directly
        if (currentPoints.length >= 4) {
          const startX = currentPoints[0];
          const endX = startX + value; // Set width directly
          const newPoints = [...currentPoints];
          newPoints[0] = startX;
          newPoints[2] = endX;
          
          // Update the arrow with the new points
          const newWidth = Math.abs(endX - startX);
          updateArrowProperties(selectedArrow.id, {
            width: newWidth,
            points: newPoints,
            skipOffset: true,
          });
          return;
        }
      }

      // CRITICAL: Scale in absolute coordinates to preserve the absolute top-left position
      // Convert relative points to absolute, scale them, then convert back to relative
      // (absoluteTopLeftX/Y already calculated above)

      // Convert points to absolute coordinates
      const absolutePoints = currentPoints.map((coord, i) =>
        i % 2 === 0 ? currentX + coord : currentY + coord
      );

      // Scale absolute points relative to the absolute top-left corner
      const scaledAbsolutePoints = absolutePoints.map((coord, i) => {
        if (i % 2 === 0) {
          // X: scale relative to absoluteTopLeftX
          return absoluteTopLeftX + (coord - absoluteTopLeftX) * scaleX;
        } else {
          // Y: scale relative to absoluteTopLeftY
          return absoluteTopLeftY + (coord - absoluteTopLeftY) * scaleY;
        }
      });

      // Calculate new bounding box from scaled absolute points
      let newAbsoluteMinX = Infinity,
        newAbsoluteMinY = Infinity,
        newAbsoluteMaxX = -Infinity,
        newAbsoluteMaxY = -Infinity;
      for (let i = 0; i < scaledAbsolutePoints.length; i += 2) {
        newAbsoluteMinX = Math.min(newAbsoluteMinX, scaledAbsolutePoints[i]);
        newAbsoluteMaxX = Math.max(newAbsoluteMaxX, scaledAbsolutePoints[i]);
        newAbsoluteMinY = Math.min(newAbsoluteMinY, scaledAbsolutePoints[i + 1]);
        newAbsoluteMaxY = Math.max(newAbsoluteMaxY, scaledAbsolutePoints[i + 1]);
      }

      // CRITICAL: Only normalize if points were already normalized
      // If points weren't normalized (after dragging middle waypoints), keep them as-is
      // to preserve the arrow's current visual position
      // Scaled points should still be detected as relative by ArrowElement (they have mixed signs, reasonable range)
      let finalPoints, newArrowX, newArrowY;
      
      if (pointsAreNormalized) {
        // Points were normalized - normalize scaled points too
        // Convert absolute points back to relative, then normalize
        const scaledRelativePoints = scaledAbsolutePoints.map((coord, i) =>
          i % 2 === 0 ? coord - newAbsoluteMinX : coord - newAbsoluteMinY
        );
        finalPoints = scaledRelativePoints;
        // After normalization, arrow position = absolute top-left (which we preserved)
        newArrowX = absoluteTopLeftX;
        newArrowY = absoluteTopLeftY;
      } else {
        // Points were NOT normalized - normalize them after scaling to prevent future issues
        // but adjust the group position to preserve the absolute top-left position
        // Convert absolute points back to relative, then normalize
        const scaledRelativePoints = scaledAbsolutePoints.map((coord, i) =>
          i % 2 === 0 ? coord - newAbsoluteMinX : coord - newAbsoluteMinY
        );
        finalPoints = scaledRelativePoints;
        // After normalization, the relative minX/minY will be 0
        // To preserve the absolute top-left: newArrowX + 0 = absoluteTopLeftX
        // So: newArrowX = absoluteTopLeftX
        newArrowX = absoluteTopLeftX;
        newArrowY = absoluteTopLeftY;
      }

      // Calculate new bounding box from final points for width/height
      let newMinX = Infinity,
        newMinY = Infinity,
        newMaxX = -Infinity,
        newMaxY = -Infinity;
      for (let i = 0; i < finalPoints.length; i += 2) {
        newMinX = Math.min(newMinX, finalPoints[i]);
        newMaxX = Math.max(newMaxX, finalPoints[i]);
        newMinY = Math.min(newMinY, finalPoints[i + 1]);
        newMaxY = Math.max(newMaxY, finalPoints[i + 1]);
      }

      const newWidth = newMaxX - newMinX;
      const newHeight = newMaxY - newMinY;

      // Detailed points logging to debug jumping issue

      updateArrowProperties(selectedArrow.id, {
        [field]: value,
        points: finalPoints, // Points are normalized only if they were already normalized, otherwise kept as-is
        x: newArrowX,
        y: newArrowY,
        width: newWidth,
        height: newHeight,
        skipOffset: true, // Prevent position recalculation since we're passing relative points
      });
      return;
    }

    // Default case
    updateArrowProperties(selectedArrow.id, { [field]: value });
  };

  const saveArrowPresetsToStorage = (presets) => {
    localStorage.setItem("sharedPresets", JSON.stringify(presets));
  };

  const saveCurrentArrowStyleAsPreset = () => {
    const usedNames = new Set(arrowPresets.map((p) => p.name));

    // Find the next unused preset name
    let index = 1;
    let name;
    do {
      name = `Arrow Preset ${index}`;
      index++;
    } while (usedNames.has(name));

    // Exclude position and other non-style properties when saving preset
    const { x, y, id, name: originalName, ...styleProperties } = selectedArrow;

    const preset = {
      type: "Arrow", // Identify this as an arrow preset
      // Store only style properties, not position or geometry
      ...styleProperties,
      id: Date.now() + Math.random(), // More unique ID generation
      name,
    };

    const updatedPresets = [...arrowPresets, preset];
    setArrowPresets(updatedPresets);
    saveArrowPresetsToStorage(updatedPresets);

    // Immediately show the edit box for the newly created preset
    setEditingPreset(preset);
  };

  const applyArrowPreset = (preset) => {
    // Filter out line-specific properties when applying line presets to arrows
    const isLinePreset = preset.type === "line";

    if (isLinePreset) {
      // For line presets applied to arrows, only copy visual properties
      // and ignore line-specific properties like startDot, endDot, etc.
      const {
        startDot,
        endDot,
        points,
        length,
        angle,
        x,
        y,
        width,
        height,
        id,
        name,
        type,
        ...visualProperties
      } = preset;

      updateArrowProperties(selectedArrow.id, {
        ...visualProperties,
        // Preserve the current arrow's ID
        id: selectedArrow.id,
        // Mark as arrow preset application
        type: "Arrow",
        name: selectedArrow.name,
      });
    } else {
      // Apply visual properties and geometry pattern, but preserve position and current size
      const {
        startDot,
        endDot,
        points: presetPoints,
        width: presetWidth,
        height: presetHeight,
        x,
        y,
        id,
        name,
        type,
        ...visualProperties
      } = preset;

      // Get current arrow's position (preserve only the position)
      const currentX = selectedArrow.x || 0;
      const currentY = selectedArrow.y || 0;

      if (
        !presetPoints ||
        presetPoints.length < 4 ||
        presetWidth === undefined ||
        presetHeight === undefined
      ) {
        // Fallback: just apply visual properties
        updateArrowProperties(selectedArrow.id, {
          ...visualProperties,
          id: selectedArrow.id,
          type: "Arrow",
          name: selectedArrow.name,
        });
        return;
      }

      // Normalize preset points to start from (0,0)
      // Find the minimum x and y in preset points
      let minX = Infinity;
      let minY = Infinity;
      for (let i = 0; i < presetPoints.length; i += 2) {
        minX = Math.min(minX, presetPoints[i]);
        minY = Math.min(minY, presetPoints[i + 1]);
      }

      // Subtract the offset so points start from (0,0) or close to it
      const normalizedPoints = [];
      for (let i = 0; i < presetPoints.length; i += 2) {
        normalizedPoints.push(
          presetPoints[i] - minX,
          presetPoints[i + 1] - minY
        );
      }

      // Apply the preset's exact geometry with normalized points
      // Only the position (x, y) is taken from the current arrow
      updateArrowProperties(selectedArrow.id, {
        ...visualProperties,
        points: normalizedPoints, // Use normalized points
        width: presetWidth, // Use preset's width
        height: presetHeight, // Use preset's height
        x: currentX, // Keep current position
        y: currentY, // Keep current position
        skipOffset: true,
        // Preserve the current arrow's ID
        id: selectedArrow.id,
        // Ensure type is passed to identify this as a preset application
        type: "Arrow",
        name: selectedArrow.name,
      });
    }
  };

  const handleDelete = () => {
    if (onDeleteArrow && id) onDeleteArrow(id);
  };

  return (
    <div>
      {/* Position and Dimensions */}
      <div
        style={{
          background: "#f5f5f5",
          borderRadius: "8px",
          padding: "10px 12px",
          marginBottom: "12px",
        }}
      >
        {/* Row 1: Position */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <label style={{ whiteSpace: "nowrap" }}>XPos</label>
            <input
              type="number"
              value={getDisplayValue(localInputValues.x)}
              min={MIN_X}
              max={MAX_X}
              onKeyDown={handleEnterKey}
              onChange={(e) => {
                const { value } = e.target;
                const inputType = e.nativeEvent?.inputType;
                setLocalInputValues((prev) => ({
                  ...prev,
                  x: value,
                }));
                if (value === "" || shouldDeferInputUpdate(inputType)) {
                  return;
                }
                const clamped = clampNumericValue(value, MIN_X, MAX_X);
                if (clamped === null) {
                  return;
                }
                handleNumberInputWithLimits("x", String(clamped), MIN_X, MAX_X);
                setLocalInputValues((prev) => ({
                  ...prev,
                  x: clamped,
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleNumberInputWithLimits("x", MIN_X, MIN_X, MAX_X);
                } else {
                  handleNumberInputWithLimits(
                    "x",
                    parseInt(val, 10),
                    MIN_X,
                    MAX_X
                  );
                }
              }}
              style={{
                width: "60px",
                padding: "6px 8px",
                marginLeft: "7px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <label style={{ whiteSpace: "nowrap" }}>YPos</label>
            <input
              type="number"
              value={getDisplayValue(localInputValues.y)}
              min={MIN_Y}
              max={MAX_Y}
              onKeyDown={handleEnterKey}
              onChange={(e) => {
                const { value } = e.target;
                const inputType = e.nativeEvent?.inputType;
                setLocalInputValues((prev) => ({
                  ...prev,
                  y: value,
                }));
                if (value === "" || shouldDeferInputUpdate(inputType)) {
                  return;
                }
                const clamped = clampNumericValue(value, MIN_Y, MAX_Y);
                if (clamped === null) {
                  return;
                }
                handleNumberInputWithLimits("y", String(clamped), MIN_Y, MAX_Y);
                setLocalInputValues((prev) => ({
                  ...prev,
                  y: clamped,
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleNumberInputWithLimits("y", MIN_Y, MIN_Y, MAX_Y);
                } else {
                  handleNumberInputWithLimits(
                    "y",
                    parseInt(val, 10),
                    MIN_Y,
                    MAX_Y
                  );
                }
              }}
              style={{
                width: "60px",
                padding: "6px 8px",
                marginLeft: "10px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>

        {/* Row 2: Dimensions */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <label style={{ whiteSpace: "nowrap" }}>Width</label>
            <input
              type="number"
              value={localInputValues.width}
              min={MIN_WIDTH}
              max={MAX_WIDTH}
              onKeyDown={handleEnterKey}
              onChange={(e) => {
                const { value } = e.target;
                const inputType = e.nativeEvent?.inputType;
                setLocalInputValues((prev) => ({
                  ...prev,
                  width: value,
                }));
                if (value === "" || shouldDeferInputUpdate(inputType)) {
                  return;
                }
                const clamped = clampNumericValue(value, MIN_WIDTH, MAX_WIDTH);
                if (clamped === null) {
                  return;
                }
                handleNumberInputWithLimits(
                  "width",
                  String(clamped),
                  MIN_WIDTH,
                  MAX_WIDTH
                );
                setLocalInputValues((prev) => ({
                  ...prev,
                  width: clamped,
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleNumberInputWithLimits(
                    "width",
                    MIN_WIDTH,
                    MIN_WIDTH,
                    MAX_WIDTH
                  );
                } else {
                  handleNumberInputWithLimits(
                    "width",
                    parseInt(val, 10),
                    MIN_WIDTH,
                    MAX_WIDTH
                  );
                }
              }}
              style={{
                width: "60px",
                padding: "6px 8px",
                marginLeft: "3px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <label style={{ whiteSpace: "nowrap" }}>Height</label>
            <input
              type="number"
              value={localInputValues.height}
              min={MIN_HEIGHT}
              max={MAX_HEIGHT}
              onKeyDown={handleEnterKey}
              onChange={(e) => {
                const { value } = e.target;
                const inputType = e.nativeEvent?.inputType;
                setLocalInputValues((prev) => ({
                  ...prev,
                  height: value,
                }));
                if (value === "" || shouldDeferInputUpdate(inputType)) {
                  return;
                }
                const clamped = clampNumericValue(
                  value,
                  MIN_HEIGHT,
                  MAX_HEIGHT
                );
                if (clamped === null) {
                  return;
                }
                handleNumberInputWithLimits(
                  "height",
                  String(clamped),
                  MIN_HEIGHT,
                  MAX_HEIGHT
                );
                setLocalInputValues((prev) => ({
                  ...prev,
                  height: clamped,
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleNumberInputWithLimits(
                    "height",
                    MIN_HEIGHT,
                    MIN_HEIGHT,
                    MAX_HEIGHT
                  );
                } else {
                  handleNumberInputWithLimits(
                    "height",
                    parseInt(val, 10),
                    MIN_HEIGHT,
                    MAX_HEIGHT
                  );
                }
              }}
              style={{
                width: "60px",
                padding: "6px 8px",
                marginLeft: "3px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
      </div>

      {/* Properties Section */}
      <div
        style={{
          background: "#f7f7f7",
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            cursor: "pointer",
          }}
          onClick={() => setIsPropertiesExpanded(!isPropertiesExpanded)}
        >
          <h4 style={{ margin: "0", fontWeight: "normal", fontSize: "14px" }}>
            Properties
          </h4>
          <svg
            width="12"
            height="7"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: isPropertiesExpanded
                ? "rotate(0deg)"
                : "rotate(180deg)",
              transition: "transform 0.2s",
            }}
          >
            <path
              d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
              fill="#6C757D"
            />
          </svg>
        </div>

        {isPropertiesExpanded && (
          <>
            {/* Stroke Size */}
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                Stroke Size
              </label>
              <input
                type="number"
                min={MIN_STROKE_WIDTH}
                max={MAX_STROKE_WIDTH}
                value={localInputValues.strokeWidth}
                onKeyDown={handleEnterKey}
                onChange={(e) => {
                  const { value } = e.target;
                  const inputType = e.nativeEvent?.inputType;
                  setLocalInputValues((prev) => ({
                    ...prev,
                    strokeWidth: value,
                  }));
                  if (value === "" || shouldDeferInputUpdate(inputType)) {
                    return;
                  }
                  const clamped = clampNumericValue(
                    value,
                    MIN_STROKE_WIDTH,
                    MAX_STROKE_WIDTH
                  );
                  if (clamped === null) {
                    return;
                  }
                  handleNumberInputWithLimits(
                    "strokeWidth",
                    String(clamped),
                    MIN_STROKE_WIDTH,
                    MAX_STROKE_WIDTH
                  );
                  setLocalInputValues((prev) => ({
                    ...prev,
                    strokeWidth: clamped,
                  }));
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    handleNumberInputWithLimits(
                      "strokeWidth",
                      MIN_STROKE_WIDTH,
                      MIN_STROKE_WIDTH,
                      MAX_STROKE_WIDTH
                    );
                  } else {
                    handleNumberInputWithLimits(
                      "strokeWidth",
                      parseInt(val, 10),
                      MIN_STROKE_WIDTH,
                      MAX_STROKE_WIDTH
                    );
                  }
                }}
                style={{
                  width: "100%",
                  padding: "4px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </div>

            {/* Bends */}
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                Bends
              </label>
              <input
                type="number"
                min="0"
                value={localInputValues.bends}
                onKeyDown={handleEnterKey}
                onChange={(e) => {
                  const { value } = e.target;
                  const inputType = e.nativeEvent?.inputType;
                  setLocalInputValues((prev) => ({
                    ...prev,
                    bends: value,
                  }));
                  if (value === "" || shouldDeferInputUpdate(inputType)) {
                    return;
                  }
                  const clamped = clampNumericValue(value, 0, undefined);
                  if (clamped === null) {
                    return;
                  }
                  handleNumberInputWithLimits(
                    "bends",
                    String(clamped),
                    0,
                    undefined
                  );
                  setLocalInputValues((prev) => ({
                    ...prev,
                    bends: clamped,
                  }));
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    handleNumberInputWithLimits(
                      "bends",
                      String(0),
                      0,
                      undefined // No max limit
                    );
                  } else {
                    handleNumberInputWithLimits(
                      "bends",
                      String(parseInt(val, 10)),
                      0,
                      undefined // No max limit
                    );
                  }
                }}
                style={{
                  width: "100%",
                  padding: "4px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </div>

            {/* Color */}
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                Color
              </label>
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                <button
                  onClick={() => {
                    const newColor = "#dc3545";
                    updateArrowProperties(selectedArrow.id, {
                      strokeColor: newColor,
                      stroke: newColor,
                    });
                    setLocalInputValues((prev) => ({
                      ...prev,
                      colorHex: newColor.toUpperCase().replace("#", ""),
                    }));
                  }}
                  style={{
                    width: "24px",
                    height: "24px",
                    backgroundColor: "#dc3545",
                    border: "1px solid #ccc",
                    borderRadius: "2px",
                    cursor: "pointer",
                  }}
                />
                <button
                  onClick={() => {
                    const newColor = "#fd7e14";
                    updateArrowProperties(selectedArrow.id, {
                      strokeColor: newColor,
                      stroke: newColor,
                    });
                    setLocalInputValues((prev) => ({
                      ...prev,
                      colorHex: newColor.toUpperCase().replace("#", ""),
                    }));
                  }}
                  style={{
                    width: "24px",
                    height: "24px",
                    backgroundColor: "#fd7e14",
                    border: "1px solid #ccc",
                    borderRadius: "2px",
                    cursor: "pointer",
                  }}
                />
                <input
                  type="color"
                  value={strokeColor}
                  onChange={(e) => {
                    const newColor = e.target.value;
                    updateArrowProperties(selectedArrow.id, {
                      strokeColor: newColor,
                      stroke: newColor,
                    });
                    // Update local hex input to stay in sync
                    setLocalInputValues((prev) => ({
                      ...prev,
                      colorHex: newColor.toUpperCase().replace("#", ""),
                    }));
                  }}
                  style={{
                    width: "40px",
                    height: "24px",
                    border: "1px solid #ccc",
                    borderRadius: "2px",
                    cursor: "pointer",
                    padding: 0,
                  }}
                />
                <input
                  type="text"
                  value={`${localInputValues.colorHex}`}
                  maxLength={10}
                  onKeyDown={handleEnterKey}
                  onChange={(e) => {
                    // Strip "Hex " prefix if present, then process hex value
                    let inputValue = e.target.value
                      .replace(/^Hex\s*/i, "")
                      .toUpperCase()
                      .replace("#", "");
                    // Limit to max 6 hex characters
                    if (inputValue.length > 6) {
                      inputValue = inputValue.slice(0, 6);
                    }
                    setLocalInputValues((prev) => ({
                      ...prev,
                      colorHex: inputValue,
                    }));
                  }}
                  onBlur={(e) => {
                    // Strip "Hex " prefix before validating
                    const hexValue = e.target.value.replace(/^Hex\s*/i, "");
                    const validColor = validateAndParseHexColor(hexValue);

                    if (validColor) {
                      // Valid hex code - update the stroke color
                      updateArrowProperties(selectedArrow.id, {
                        strokeColor: validColor,
                        stroke: validColor,
                      });
                    } else {
                      // Invalid hex code - revert to current color
                      const currentHex = strokeColor
                        .toUpperCase()
                        .replace("#", "");
                      setLocalInputValues((prev) => ({
                        ...prev,
                        colorHex: currentHex,
                      }));
                    }
                  }}
                  placeholder="Hex 000000"
                  style={{
                    flex: 1,
                    padding: "4px 8px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "white",
                    fontSize: "12px",
                  }}
                />
              </div>
            </div>
            <button
              onClick={saveCurrentArrowStyleAsPreset}
              style={{
                background: "#f7f7f7",
                border: "none",
                width: "100%",
                color: "#007bff",
                fontSize: "13px",
              }}
            >
              <span
                style={{
                  fontSize: "16px",
                  marginRight: "8px",
                }}
              >
                +
              </span>
              Add These Properties as a Style
            </button>
          </>
        )}
      </div>

      {/* Add to Preset Button */}
      <div
        style={{
          marginBottom: "12px",
          display: "flex",
          alignItems: "center",
        }}
      ></div>

      {/* Preset Controls */}
      <div
        style={{
          background: "#f7f7f7",
          borderRadius: "5px",
          marginTop: "-10px",
          padding: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setIsStylesExpanded(!isStylesExpanded)}
        >
          <h4 style={{ margin: "0", fontWeight: "normal" }}>Styles</h4>
          <svg
            width="20"
            height="12"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: isStylesExpanded ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.2s",
            }}
          >
            <path
              d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
              fill="#6C757D"
            />
          </svg>
        </div>

        {isStylesExpanded && (
          <>
            {/* List Presets */}
            {arrowPresets.map((preset) => (
              <div
                key={preset.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: "1px solid #eee",
                }}
              >
                <button
                  onClick={() => applyArrowPreset(preset)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    flexGrow: 1,
                    padding: 0,
                    fontSize: "1rem",
                  }}
                >
                  {preset.name}
                </button>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={() => setEditingPreset(preset)}
                    title="Edit"
                    style={{
                      backgroundColor: "#007bff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      padding: "2px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "22px",
                      height: "22px",
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_15909_16836)">
                        <path
                          d="M11.6263 1.45481C11.6964 1.5251 11.7357 1.6203 11.7357 1.71956C11.7357 1.81881 11.6964 1.91401 11.6263 1.98431L10.844 2.76731L9.34404 1.26731L10.1263 0.484307C10.1966 0.414005 10.292 0.374512 10.3914 0.374512C10.4908 0.374512 10.5862 0.414005 10.6565 0.484307L11.6263 1.45406V1.45481ZM10.3138 3.29681L8.81379 1.79681L3.70404 6.90731C3.66276 6.94858 3.63168 6.99891 3.61329 7.05431L3.00954 8.86481C2.99859 8.89781 2.99703 8.93321 3.00505 8.96705C3.01306 9.00089 3.03033 9.03184 3.05492 9.05643C3.07951 9.08102 3.11045 9.09828 3.14429 9.1063C3.17813 9.11431 3.21353 9.11276 3.24654 9.10181L5.05704 8.49806C5.11236 8.47988 5.16269 8.44906 5.20404 8.40806L10.3138 3.29681Z"
                          fill="white"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M0.75 10.125C0.75 10.4234 0.868526 10.7095 1.0795 10.9205C1.29048 11.1315 1.57663 11.25 1.875 11.25H10.125C10.4234 11.25 10.7095 11.1315 10.9205 10.9205C11.1315 10.7095 11.25 10.4234 11.25 10.125V5.625C11.25 5.52554 11.2105 5.43016 11.1402 5.35984C11.0698 5.28951 10.9745 5.25 10.875 5.25C10.7755 5.25 10.6802 5.28951 10.6098 5.35984C10.5395 5.43016 10.5 5.52554 10.5 5.625V10.125C10.5 10.2245 10.4605 10.3198 10.3902 10.3902C10.3198 10.4605 10.2245 10.5 10.125 10.5H1.875C1.77554 10.5 1.68016 10.4605 1.60984 10.3902C1.53951 10.3198 1.5 10.2245 1.5 10.125V1.875C1.5 1.77554 1.53951 1.68016 1.60984 1.60984C1.68016 1.53951 1.77554 1.5 1.875 1.5H6.75C6.84946 1.5 6.94484 1.46049 7.01517 1.39017C7.08549 1.31984 7.125 1.22446 7.125 1.125C7.125 1.02554 7.08549 0.930161 7.01517 0.859835C6.94484 0.789509 6.84946 0.75 6.75 0.75H1.875C1.57663 0.75 1.29048 0.868526 1.0795 1.0795C0.868526 1.29048 0.75 1.57663 0.75 1.875V10.125Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_15909_16836">
                          <rect width="12" height="12" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      const filtered = arrowPresets.filter(
                        (p) => p.id !== preset.id
                      );
                      setArrowPresets(filtered);
                      saveArrowPresetsToStorage(filtered);
                      // Clear editing state if the deleted preset is being edited
                      if (editingPreset && editingPreset.id === preset.id) {
                        setEditingPreset(null);
                      }
                    }}
                    title="Delete"
                    style={{
                      backgroundColor: "#dc3545",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      padding: "2px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "22px",
                      height: "22px",
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="18" height="18" rx="4" fill="#DC3545" />
                      <path
                        d="M5.875 14.5C5.53125 14.5 5.23698 14.3803 4.99219 14.141C4.7474 13.9016 4.625 13.6139 4.625 13.2778V5.33333H4V4.11111H7.125V3.5H10.875V4.11111H14V5.33333H13.375V13.2778C13.375 13.6139 13.2526 13.9016 13.0078 14.141C12.763 14.3803 12.4688 14.5 12.125 14.5H5.875ZM7.125 12.0556H8.375V6.55556H7.125V12.0556ZM9.625 12.0556H10.875V6.55556H9.625V12.0556Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}

            {editingPreset && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                }}
              >
                <h4>Edit Preset: {editingPreset.name}</h4>
                <input
                  type="text"
                  value={editingPreset.name}
                  onChange={(e) =>
                    setEditingPreset({
                      ...editingPreset,
                      name: e.target.value,
                    })
                  }
                  placeholder="Preset name"
                  style={{
                    width: "100%",
                    padding: "6px",
                    border: "1px solid #ccc",
                    marginBottom: "12px",
                  }}
                />

                <button
                  onClick={() => {
                    const trimmedName = editingPreset.name.trim();

                    // Check for duplicate name (excluding the current editing one)
                    const isDuplicate = arrowPresets.some(
                      (p) =>
                        p.id !== editingPreset.id &&
                        p.name.trim().toLowerCase() ===
                          trimmedName.toLowerCase()
                    );

                    if (isDuplicate) {
                      alert(
                        "A preset with this name already exists. Please choose a different name."
                      );
                      return;
                    }

                    const updated = arrowPresets.map((p) =>
                      p.id === editingPreset.id
                        ? { ...editingPreset, name: trimmedName }
                        : p
                    );

                    setArrowPresets(updated);
                    saveArrowPresetsToStorage(updated);
                    setEditingPreset(null);
                  }}
                  style={{
                    backgroundColor: "#2563eb",
                    color: "white",
                    padding: "6px 12px",
                    borderRadius: "4px",
                    border: "none",
                    cursor: "pointer",
                    marginRight: "8px",
                  }}
                >
                  Save Changes
                </button>

                <button
                  onClick={() => setEditingPreset(null)}
                  style={{
                    backgroundColor: "#e5e7eb",
                    color: "#111827",
                    padding: "6px 12px",
                    borderRadius: "4px",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Button */}
      <div style={{ marginTop: "16px" }}>
        <button
          onClick={handleDelete}
          style={{
            width: "100%",
            height: "40px",
            backgroundColor: "white",
            borderRadius: "5px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: "2px solid red",
            color: "red",
            marginTop: "10px",
          }}
        >
          Delete Stroke
        </button>
      </div>
    </div>
  );
}

ArrowEditor.propTypes = {
  selectedArrow: PropTypes.shape({
    id: PropTypes.string,
    isDashed: PropTypes.bool,
    strokeWidth: PropTypes.number,
    bend: PropTypes.number,
    angle: PropTypes.number,
    strokeColor: PropTypes.string,
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    rotation: PropTypes.number,
  }),
  updateArrowProperties: PropTypes.func.isRequired,
  waypointCount: PropTypes.number,
  onDeleteArrow: PropTypes.func,
};

export default ArrowEditor;
